import * as React from 'react';
import {
  AppBar, Box, Toolbar, Typography, IconButton, Button, MenuItem,
  Drawer, Divider, Container
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Link, useNavigate } from 'react-router-dom';
import ColorModeIconDropdown from '../shared-theme/ColorModeIconDropdown';
import { useAuth } from '../Context/AuthContext';
import { brandGradient } from '../shared-theme/themePrimitives';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: '8px 12px',
}));

const AppAppBar = () => {
  const [open, setOpen] = React.useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const ctaStyle = {
    fontWeight: 600,
    borderRadius: '999px',
    px: 2,
  };

  const NavLinks = ({ isMobile = false, closeDrawer }) => {
    const links = [
      { to: '/', label: 'Home' },
      { to: '/search', label: 'Search' },
      // { to: '/about', label: 'About' },
      // { to: '/pricing', label: 'Pricing' },
    ];

    if (user?.role === 'brand') {
      links.push(
        { to: '/brand-profile', label: 'Brand Profile' },
        { to: '/Chat-Bot', label: 'AI Bot' },
        { to: '/live-search', label: 'Live AI Search' }
      );
    }

    if (user?.role === 'creator') {
      links.push({ to: '/influencer-profile', label: 'Influencer Profile' });
    }

    return (
      <>
        {links.map((link) =>
          isMobile ? (
            <MenuItem
              key={link.to}
              component={Link}
              to={link.to}
              onClick={closeDrawer}
            >
              {link.label}
            </MenuItem>
          ) : (
            <Button
              key={link.to}
              component={Link}
              to={link.to}
              variant="text"
              color="info"
              size="small"
            >
              {link.label}
            </Button>
          )
        )}
      </>
    );
  };

  return (
    <AppBar
      position="static"
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: 'calc(var(--template-frame-height, 0px) + 28px)',
      }}
    >
      <Container maxWidth="lg">
        <StyledToolbar variant="dense" disableGutters>
          {/* Left side: Logo + Links */}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                background: brandGradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              The Creator Match
            </Typography>
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <NavLinks />
            </Box>
          </Box>

          {/* Right side: Auth Buttons */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 1.5,
              alignItems: 'center',
            }}
          >
            {user ? (
              <>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'text.secondary' }}
                >
                  Welcome, {user.name || user.email}
                </Typography>
                <Button
                  onClick={() => navigate('/change-password')}
                  color="secondary"
                  size="small"
                  sx={ctaStyle}
                >
                  Change Password
                </Button>
                <Button
                  onClick={() => {
                    logout();
                    navigate('/');
                  }}
                  color="error"
                  size="small"
                  variant="outlined"
                  sx={ctaStyle}
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button component={Link} to="/sign-in" color="primary" size="small" sx={ctaStyle}>
                  Sign in
                </Button>
                <Button
                  component={Link}
                  to="/sign-up"
                  state={{ role: 'creator' }}
                  variant="outlined"
                  color="secondary"
                  size="small"
                  sx={ctaStyle}
                >
                  Join as Creator
                </Button>
                <Button
                  component={Link}
                  to="/sign-up"
                  state={{ role: 'brand' }}
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{ ...ctaStyle, backgroundImage: brandGradient, color: '#fff' }}
                >
                  Join as Brand
                </Button>
              </>
            )}
            <ColorModeIconDropdown />
          </Box>

          {/* Mobile Menu Toggle */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
            <ColorModeIconDropdown size="medium" />
            <IconButton aria-label="menu" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="top"
              open={open}
              onClose={toggleDrawer(false)}
              PaperProps={{ sx: { top: 'var(--template-frame-height, 0px)' } }}
            >
              <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>
                <NavLinks isMobile closeDrawer={toggleDrawer(false)} />
                <Divider sx={{ my: 2 }} />
                {user ? (
                  <>
                    <MenuItem>
                      <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Welcome, {user.name || user.email}
                      </Typography>
                    </MenuItem>
                    <MenuItem>
                      <Button
                        onClick={() => {
                          navigate('/change-password');
                          toggleDrawer(false)();
                        }}
                        fullWidth
                        color="secondary"
                        sx={ctaStyle}
                      >
                        Change Password
                      </Button>
                    </MenuItem>
                    <MenuItem>
                      <Button
                        onClick={() => {
                          logout();
                          navigate('/');
                          toggleDrawer(false)();
                        }}
                        fullWidth
                        variant="outlined"
                        color="error"
                        sx={ctaStyle}
                      >
                        Logout
                      </Button>
                    </MenuItem>
                  </>
                ) : (
                  <>
                    <MenuItem>
                      <Button
                        component={Link}
                        to="/sign-up"
                        state={{ role: 'brand' }}
                        onClick={toggleDrawer(false)}
                        fullWidth
                        variant="contained"
                        sx={{ ...ctaStyle, backgroundImage: brandGradient, color: '#fff' }}
                      >
                        Join as Brand
                      </Button>
                    </MenuItem>
                    <MenuItem>
                      <Button
                        component={Link}
                        to="/sign-up"
                        state={{ role: 'creator' }}
                        onClick={toggleDrawer(false)}
                        fullWidth
                        variant="outlined"
                        color="secondary"
                        sx={ctaStyle}
                      >
                        Join as Creator
                      </Button>
                    </MenuItem>
                    <MenuItem>
                      <Button
                        component={Link}
                        to="/sign-in"
                        onClick={toggleDrawer(false)}
                        fullWidth
                        color="primary"
                        sx={ctaStyle}
                      >
                        Sign in
                      </Button>
                    </MenuItem>
                  </>
                )}
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </Container>
    </AppBar>
  );
};

export default AppAppBar;
